/**
 * Hook to fetch options for cell editor dropdowns based on linked data sources
 */

import { useMemo, useEffect, useState } from "react";
import { LinkedDataSourceType, FieldMetadata, StatusItemType } from "@/lib/dataSources/types";
import { CellOption } from "@/components/table-main/cell-editors/types";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useTagsDataContext } from "@/providers/TagsDataProvider";
import { useTypesDataContext } from "@/providers/TypesDataProvider";
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { useGlossaryBlueprintsDataContext } from "@/providers/GlossaryBlueprintsDataProvider";
import { statusStore, StatusWorkflow, getDefaultWorkflow } from "@/lib/stores/statusStore";
import { getGameMembers, subscribeToUserData } from "@/lib/stores/unifiedUserStore";
import { getFolders, subscribeFolders, Folder } from "@/lib/stores/folderStore";
import { getCachedDialogs } from "@/lib/stores/dialogStore";

// Fallback status options (used when store has no data)
const DEFAULT_STATUS_OPTIONS: CellOption[] = Object.entries(getDefaultWorkflow()).map(
  ([key, value]) => ({
    label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
    value: key,
    color: value.color,
  })
);

interface UseFieldOptionsReturn {
  options: CellOption[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to get options for a field based on its linked data source
 */
export function useFieldOptions(
  linkedDataSource?: LinkedDataSourceType,
  fieldMetadata?: FieldMetadata
): UseFieldOptionsReturn {
  const { selectedGame } = useSimplifiedUserData();
  const { tags, isLoading: tagsLoading } = useTagsDataContext();
  const { types, isLoading: typesLoading } = useTypesDataContext();
  const { glossaryItems, isLoading: objectsLoading } = useGlossaryDataContext();
  const { glossaryBlueprints, isLoading: templatesLoading } = useGlossaryBlueprintsDataContext();
  
  // For users, we'll use a local state since there's no global provider
  const [users, setUsers] = useState<CellOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  // For statuses, subscribe to the status store
  const [statusOptions, setStatusOptions] = useState<CellOption[]>(DEFAULT_STATUS_OPTIONS);
  const [statusesLoading, setStatusesLoading] = useState(false);
  
  // For folders, we'll use local state
  const [folderOptions, setFolderOptions] = useState<CellOption[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  
  // Get the status item type from field metadata, default to 'objects'
  const statusItemType: StatusItemType = fieldMetadata?.statusItemType || 'objects';

  // Fetch users from unified user store
  useEffect(() => {
    if (linkedDataSource !== "users" || !selectedGame?.id) return;
    
    const loadUsers = () => {
      setUsersLoading(true);
      
      // Get game members from the unified user store (already has profile data)
      const gameMembers = getGameMembers(selectedGame.id);
      
      const userOptions: CellOption[] = gameMembers.map((user) => ({
        label: user.username || user.full_name || user.email || 'Unknown User',
        value: user.id,
        avatar: user.avatar_url || undefined,
      }));
      
      setUsers(userOptions);
      setUsersLoading(false);
    };
    
    // Initial load
    loadUsers();
    
    // Subscribe to store changes
    const unsubscribe = subscribeToUserData(loadUsers);
    
    return () => {
      unsubscribe();
    };
  }, [linkedDataSource, selectedGame?.id]);

  // Subscribe to status store for status options
  useEffect(() => {
    if (linkedDataSource !== "statuses") return;
    
    const convertWorkflowToOptions = (workflow: StatusWorkflow): CellOption[] => {
      return Object.entries(workflow).map(([key, value]) => ({
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
        value: key,
        color: value.color,
      }));
    };
    
    const updateStatusOptions = () => {
      const statusData = statusStore.getData();
      if (statusData.length > 0) {
        // Find the status configuration for the current game
        const relevantStatus = selectedGame?.id
          ? statusData.find((s) => s.game_id === selectedGame.id)
          : statusData[0];
        
        if (relevantStatus) {
          const workflow = relevantStatus[statusItemType] || {};
          const options = convertWorkflowToOptions(workflow);
          setStatusOptions(options.length > 0 ? options : DEFAULT_STATUS_OPTIONS);
        } else {
          setStatusOptions(DEFAULT_STATUS_OPTIONS);
        }
      } else {
        setStatusOptions(DEFAULT_STATUS_OPTIONS);
      }
      setStatusesLoading(false);
    };
    
    setStatusesLoading(true);
    
    // Initial load
    updateStatusOptions();
    
    // Subscribe to changes
    const unsubscribe = statusStore.subscribe(updateStatusOptions);
    
    return () => {
      unsubscribe();
    };
  }, [linkedDataSource, selectedGame?.id, statusItemType]);

  // Fetch folders from folder store
  useEffect(() => {
    if (linkedDataSource !== "folders" || !selectedGame?.id) return;
    
    // Get the item type from field metadata, default to 'library'
    const itemType = fieldMetadata?.folderItemType || 'library';
    
    // Helper to flatten folder hierarchy into options with path prefixes
    const flattenFolders = (folders: Folder[], parentPath: string = ''): CellOption[] => {
      const options: CellOption[] = [];
      
      for (const folder of folders) {
        const path = parentPath ? `${parentPath}/${folder.name}` : `/${folder.name}`;
        options.push({
          label: path,
          value: folder.id,
        });
        
        // Recursively add subfolders
        if (folder.subfolder && folder.subfolder.length > 0) {
          options.push(...flattenFolders(folder.subfolder, path));
        }
        // Also check 'children' property for compatibility
        if (folder.children && folder.children.length > 0) {
          options.push(...flattenFolders(folder.children, path));
        }
      }
      
      return options;
    };
    
    const loadFolders = () => {
      setFoldersLoading(true);
      
      const gameFolders = getFolders(selectedGame.id, itemType);
      const options = flattenFolders(gameFolders);
      
      // Add "No folder" option at the beginning
      setFolderOptions([
        { label: '(No folder)', value: '' },
        ...options
      ]);
      setFoldersLoading(false);
    };
    
    // Initial load
    loadFolders();
    
    // Subscribe to folder store changes
    const unsubscribe = subscribeFolders(loadFolders);
    
    return () => {
      unsubscribe();
    };
  }, [linkedDataSource, selectedGame?.id, fieldMetadata?.folderItemType]);

  // Build options based on linked data source
  const options = useMemo((): CellOption[] => {
    // If static options are provided in field metadata, use those
    if (fieldMetadata?.options && fieldMetadata.options.length > 0) {
      return fieldMetadata.options.map((opt) => ({
        label: opt.label,
        value: String(opt.value),
      }));
    }

    // Otherwise, fetch from linked data source
    switch (linkedDataSource) {
      case "users":
        return users;

      case "tags":
        return (tags || []).map((tag) => ({
          label: tag.name,
          value: tag.id,
          color: tag.color || undefined,
        }));

      case "types": {
        // For types, we need to return type elements, not the types themselves
        // If filterOptions is provided, only show elements from those types
        const filterTypeIds = (fieldMetadata as any)?.filterOptions;
        let typeElements: CellOption[] = [];
        
        // First pass: try to get elements from filtered types
        let filteredTypes = types || [];
        if (filterTypeIds && Array.isArray(filterTypeIds) && filterTypeIds.length > 0) {
          filteredTypes = types.filter(type => filterTypeIds.includes(type.id));
        }
        
        filteredTypes.forEach((type) => {
          // Add all elements from this type
          if (type.data && Array.isArray(type.data)) {
            type.data.forEach((element: any) => {
              typeElements.push({
                label: element.name,
                value: element.id,
                // Optionally show which type this element belongs to
                description: type.name,
              });
            });
          }
        });
        
        return typeElements;
      }

      case "objects": {
        // If filterOptions is provided (blueprint/template IDs), only show objects from those templates
        const filterBlueprintIds = (fieldMetadata as any)?.filterOptions;
        
        let filteredObjects = glossaryItems || [];
        if (filterBlueprintIds && Array.isArray(filterBlueprintIds) && filterBlueprintIds.length > 0) {
          filteredObjects = filteredObjects.filter(item => 
            item.blueprint_id && filterBlueprintIds.includes(item.blueprint_id)
          );
        }
        
        return filteredObjects.map((item) => ({
          label: item.name,
          value: item.id,
        }));
      }

      case "templates":
        return (glossaryBlueprints || []).map((template) => ({
          label: template.name,
          value: template.id,
        }));

      case "statuses":
        return statusOptions;

      case "folders":
        return folderOptions;

      case "dialogs": {
        if (!selectedGame?.id) return [];
        
        // If filterOptions is provided (tag names), only show dialogs with those tags
        const filterTags = (fieldMetadata as any)?.filterOptions;
        
        let dialogs = getCachedDialogs(selectedGame.id);
        if (filterTags && Array.isArray(filterTags) && filterTags.length > 0) {
          dialogs = dialogs.filter(dialog =>
            dialog.tags && dialog.tags.some(tag => filterTags.includes(tag))
          );
        }
        
        return dialogs.map((dialog) => ({
          label: dialog.name,
          value: dialog.id,
        }));
      }

      default:
        return [];
    }
  }, [linkedDataSource, fieldMetadata?.options, users, tags, types, glossaryItems, glossaryBlueprints, statusOptions, folderOptions, selectedGame?.id]);

  // Calculate loading state
  const isLoading = useMemo(() => {
    switch (linkedDataSource) {
      case "users":
        return usersLoading;
      case "tags":
        return tagsLoading;
      case "types":
        return typesLoading;
      case "objects":
        return objectsLoading;
      case "templates":
        return templatesLoading;
      case "statuses":
        return statusesLoading;
      case "folders":
        return foldersLoading;
      default:
        return false;
    }
  }, [linkedDataSource, usersLoading, tagsLoading, typesLoading, objectsLoading, templatesLoading, statusesLoading, foldersLoading]);

  return {
    options,
    isLoading,
    error: null,
  };
}

/**
 * Get display value for a linked field
 * Converts IDs to display labels
 */
export function useFieldDisplayValue(
  value: any,
  linkedDataSource?: LinkedDataSourceType,
  fieldMetadata?: FieldMetadata
): string {
  const { options } = useFieldOptions(linkedDataSource, fieldMetadata);

  return useMemo(() => {
    if (!value) return "";

    // Handle array values (multi-select)
    if (Array.isArray(value)) {
      return value
        .map((v) => {
          const option = options.find((opt) => opt.value === v);
          return option?.label || v;
        })
        .join(", ");
    }

    // Handle single values
    const option = options.find((opt) => opt.value === value);
    return option?.label || String(value);
  }, [value, options]);
}

export default useFieldOptions;

